/**
 * Utilitário para upload de arquivos ao Cloudflare R2 via Supabase Edge Function.
 * Função standalone — pode ser usada fora de componentes React.
 *
 * O arquivo é enviado diretamente para R2 e apenas a URL pública é retornada
 * para ser salva no Supabase.
 */

export interface R2UploadResult {
  url: string;
  key: string;
  originalName: string;
  size: number;
  type: string;
}

// URL fixa do Supabase pessoal do usuário (projeto definitivo)
const EDGE_FUNCTION_URL = "https://egszaqptmlunwcfujxxp.supabase.co/functions/v1/upload-to-r2";

/**
 * Faz upload de um arquivo para o Cloudflare R2 via Supabase Edge Function.
 *
 * @param file - Arquivo a ser enviado (File, Blob, ou FileList)
 * @param folder - Pasta/namespace dentro do bucket (ex: "avatars", "portfolio", "pdfs")
 * @returns Promise<R2UploadResult> com a URL pública e metadados do arquivo
 *
 * @example
 * const result = await uploadToR2(fileInput.files[0], "avatars");
 * await supabase.from("profiles").update({ avatar_url: result.url }).eq("id", userId);
 */
export async function uploadToR2(
  file: File,
  folder = "uploads"
): Promise<R2UploadResult> {
  const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || "";

  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", folder);

  const response = await fetch(EDGE_FUNCTION_URL, {
    method: "POST",
    body: formData,
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
  });

  if (!response.ok) {
    let errMessage = `Upload failed: ${response.status}`;
    try {
      const errData = (await response.json()) as { error?: string };
      if (errData.error) errMessage = errData.error;
    } catch {
      // ignore parse error
    }
    throw new Error(errMessage);
  }

  const data = (await response.json()) as R2UploadResult & { success?: boolean };

  if (!data.success || !data.url) {
    throw new Error("Invalid response from upload server");
  }

  return {
    url: data.url,
    key: data.key,
    originalName: data.originalName,
    size: data.size,
    type: data.type,
  };
}

/**
 * Faz upload de múltiplos arquivos para o R2.
 *
 * @param files - Array de File ou FileList
 * @param folder - Pasta destino no bucket R2
 * @returns Array de resultados de upload (erros retornam null no array)
 */
export async function uploadMultipleToR2(
  files: File[] | FileList,
  folder = "uploads"
): Promise<(R2UploadResult | null)[]> {
  const fileArray = Array.isArray(files) ? files : Array.from(files);
  const results = await Promise.all(
    fileArray.map(async (file) => {
      try {
        return await uploadToR2(file, folder);
      } catch {
        return null;
      }
    })
  );
  return results;
}